import { Divider, Stack } from '@mui/material';
import { SexUpper } from 'primary-schema';
import { z } from 'zod';

import { api } from 'common';

import { OrderJobParamsFormFields, OrderJobParamsSchema } from './Common/Orders';
import { OrderProcedures } from './OrderProcedures';
import {
  type AsynchronousJobVariant,
  OptionalStringSchema,
  PatientSexUpperSchema,
  RequiredStringSchema,
} from './common';
import { BlockValueTemplateField } from '../../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockSelectField } from '../../../ConfigFields/SelectField';

import JobKind = api.RpaJob.Kind;

export const ExportReferralDocumentVariant: AsynchronousJobVariant = {
  schema: z.object({
    jobKind: z.literal(JobKind.ExportReferralDocument),
    payload: OrderJobParamsSchema.merge(
      z.object({
        patientFirstName: RequiredStringSchema,
        patientLastName: RequiredStringSchema,
        patientDateOfBirth: RequiredStringSchema,
        patientSex: PatientSexUpperSchema,
        patientRace: OptionalStringSchema,
        patientPhoneNumber: OptionalStringSchema,
        patientAddress: z
          .object({
            address: OptionalStringSchema,
            unitNumber: OptionalStringSchema,
            city: OptionalStringSchema,
            state: OptionalStringSchema,
            zip: OptionalStringSchema,
          })
          .optional(),
        referralPriority: OptionalStringSchema,
        department: OptionalStringSchema,
        patientOrderCreated: OptionalStringSchema,
        referredByProviderFirstName: OptionalStringSchema,
        referredByProviderLastName: OptionalStringSchema,
        referredByProviderEhrId: OptionalStringSchema,
        attendingProviderFirstName: OptionalStringSchema,
        attendingProviderLastName: OptionalStringSchema,
        attendingProviderNpi: OptionalStringSchema,
        procedures: z.union([
          z
            .array(
              z.object({
                description: RequiredStringSchema,
                cptCode: OptionalStringSchema,
                areaOfInterest: OptionalStringSchema,
              })
            )
            .optional(),
          OptionalStringSchema,
        ]),
        fileName: OptionalStringSchema,
        fileReference: OptionalStringSchema,
      })
    ),
  }),
  Config: () => {
    return (
      <Stack spacing={2}>
        <BlockValueTemplateField
          fieldName="config.payload.patientFirstName"
          label="Patient First Name"
          helperText="Enter the reference to the patient's first name. e.g. `{{patient.data.firstName}}`"
          placeholder="e.g. {{patient.data.firstName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientLastName"
          label="Patient Last Name"
          helperText="Enter the reference to the patient's last name. e.g. `{{patient.data.lastName}}`"
          placeholder="e.g. {{patient.data.lastName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientDateOfBirth"
          label="Patient Date of Birth"
          helperText="Enter the reference to the patient's date of birth. e.g. `{{patient.data.dateOfBirth}}`"
          placeholder="e.g. {{patient.data.dateOfBirth}}"
        />
        <BlockSelectField
          fieldName="config.payload.patientSex"
          label="Patient Sex"
          helperText="Select the patient's sex, or use a flow template (e.g. `{{patient.data.sex}}`) to reference a dynamic value."
          fieldVariant="SelectOrBlockValueTemplate"
          options={sexOptions}
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientRace"
          label="Patient Race"
          helperText="Enter the reference to the patient's race. e.g. `{{patient.data.race}}`"
          placeholder="e.g. {{patient.data.race}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientPhoneNumber"
          label="Patient Phone Number"
          helperText="Enter the reference to the patient's phone number. e.g. `{{patient.data.phoneNumber}}`"
          placeholder="e.g. {{patient.data.phoneNumber}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientAddress.address"
          label="Patient Address"
          helperText="Enter the reference to the patient's address. e.g. `{{patient.data.address}}`"
          placeholder="e.g. {{patient.data.address}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientAddress.unitNumber"
          label="Patient Address Unit Number"
          helperText="Enter the reference to the patient's address unit number. e.g. `{{patient.data.unitNumber}}`"
          placeholder="e.g. {{patient.data.unitNumber}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientAddress.city"
          label="Patient Address City"
          helperText="Enter the reference to the patient's address city. e.g. `{{patient.data.city}}`"
          placeholder="e.g. {{patient.data.city}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientAddress.state"
          label="Patient Address State"
          helperText="Enter the reference to the patient's address state. e.g. `{{patient.data.state}}`"
          placeholder="e.g. {{patient.data.state}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientAddress.zip"
          label="Patient Address ZIP"
          helperText="Enter the reference to the patient's address ZIP code. e.g. `{{patient.data.zip}}`"
          placeholder="e.g. {{patient.data.zip}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.referralPriority"
          label="Referral Priority"
          helperText="Enter the reference to the referral priority. e.g. `{{referralOrder.data.priority}}`"
          placeholder="e.g. {{referralOrder.data.priority}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.department"
          label="Department"
          helperText="Enter the reference to the department. e.g. `{{referralOrder.data.department}}`"
          placeholder="e.g. {{referralOrder.data.department}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patientOrderCreated"
          label="Patient Order Created"
          helperText="Enter the reference to the patient order created date. e.g. `{{patientOrder.data.created}}`"
          placeholder="e.g. {{patientOrder.data.created}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.referredByProviderFirstName"
          label="Referred By Provider First Name"
          helperText="Enter the reference to the referred by provider first name. e.g. `{{referralOrder.data.referredByProviderFirstName}}`"
          placeholder="e.g. {{referralOrder.data.referredByProviderFirstName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.referredByProviderLastName"
          label="Referred By Provider Last Name"
          helperText="Enter the reference to the referred by provider last name. e.g. `{{referralOrder.data.referredByProviderLastName}}`"
          placeholder="e.g. {{referralOrder.data.referredByProviderLastName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.referredByProviderEhrId"
          label="Referred By Provider EHR ID"
          helperText="Enter the reference to the referred by provider EHR ID. e.g. `{{referralOrder.data.referredByProviderEhrId}}`"
          placeholder="e.g. {{referralOrder.data.referredByProviderEhrId}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.attendingProviderFirstName"
          label="Attending Provider First Name"
          helperText="Enter the reference to the attending provider first name. e.g. `{{encounter.data.attendingProviderFirstName}}`"
          placeholder="e.g. {{encounter.data.attendingProviderFirstName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.attendingProviderLastName"
          label="Attending Provider Last Name"
          helperText="Enter the reference to the attending provider last name. e.g. `{{encounter.data.attendingProviderLastName}}`"
          placeholder="e.g. {{encounter.data.attendingProviderLastName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.attendingProviderNpi"
          label="Attending Provider NPI"
          helperText="Enter the reference to the attending provider NPI. e.g. `{{encounter.data.attendingProviderNpi}}`"
          placeholder="e.g. {{encounter.data.attendingProviderNpi}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.fileName"
          label="Document Filename"
          helperText="Enter the reference to the document's filename"
        />
        <BlockValueTemplateField
          fieldName="config.payload.fileReference"
          label="Document File Reference"
          helperText="Enter the reference to the base64 document file"
        />
        <Divider />
        <OrderProcedures />
        <Divider />
        <OrderJobParamsFormFields />
      </Stack>
    );
  },
};

const sexOptions = Object.values(SexUpper).map((sex) => ({
  label: sex,
  value: sex,
}));
